/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2021, 2022
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/
import React, { useState, useRef, useEffect } from 'react';
import { TextInput, Button } from 'carbon-components-react';
import PropTypes from 'prop-types';
import { CodeViewer } from './CodeViewer';
import classNames from 'classnames';
import ExpandableDisplay from './ExpandableDisplay';
import { useTranslation } from 'react-i18next';

const TestToolWebsocket = ({ websocket }) => {
  const [messages, updateMessages] = useState('');
  const [message, updateMessage] = useState('');
  const { t } = useTranslation();
  const url = useRef(websocket ? websocket.url : '');

  useEffect(() => {
    if (!websocket || url.current !== websocket.url) {
      updateMessages([]);
      url.current = websocket ? websocket.url : '';
    }
  }, [websocket]);

  if (websocket) {
    websocket.onmessage = function (message) {
      updateMessages(messages + t('received') + ': ' + message.data + '\n');
    };
  }

  const updateMessageValue = (event) => {
    updateMessage(event.target.value);
  };

  const sendMessage = () => {
    if (message !== '' && websocket.readyState === 1) {
      websocket.send(message);
      updateMessages(messages + t('sent') + ': ' + message + '\n');
      updateMessage('');
    }
  };

  return (
    <div className="testToolWebsocket">
      <div className={classNames('testToolSection', 'testToolWebsocketRequest')}>
        <div className="labelText">{t('request')}</div>
        <div>
          <div className={classNames('connection-status')} data-testid="connection-status">
            {t('status')}:{!websocket && <span> {t('connect_failed')}</span>}
            {websocket && websocket.readyState === 0 && <span> {t('waiting_connection')}</span>}
            {websocket && websocket.readyState === 1 && <span> {t('connected')}</span>}
            {websocket && websocket.readyState === 2 && <span> {t('connection_error')}</span>}
            {websocket && websocket.readyState === 3 && <span> {t('connection_closed')}</span>}
          </div>
          <div>
            <TextInput
              id="message-input"
              className="message-input"
              labelText={t('message')}
              placeholder={t('enter_message')}
              role="textbox"
              value={message}
              onChange={updateMessageValue}
              disabled={!websocket || websocket.readyState !== 1}
            />
            <Button id="send-message" className="send-message" data-testid="send" onClick={sendMessage} disabled={!websocket || websocket.readyState !== 1}>
              {t('send')}
            </Button>
          </div>
        </div>
      </div>
      {messages.length > 0 && (
        <div className={classNames('testToolSection', 'testToolWebsocketMessages')}>
          <div className="labelText">{t('message_log')}</div>
          <div id="message-list" className={classNames('message-list', 'bx--snippet', 'bx--snippet--multi', 'bx--snippet--wraptext')}>
            <ExpandableDisplay copyableContent={messages}>
              <CodeViewer data-testid="message-list" highlight={false}>
                {messages}
              </CodeViewer>
            </ExpandableDisplay>
          </div>
        </div>
      )}
    </div>
  );
};

TestToolWebsocket.propTypes = {
  websocket: PropTypes.object,
  api: PropTypes.object
};

export default TestToolWebsocket;
